import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { TrendingUp, CheckCircle2, Wallet, FileText, IndianRupee, Smile, AlertTriangle, Target } from "lucide-react";

// Validated categorical palette (dataviz skill reference instance, 4-slot subset —
// passes CVD/contrast checks for this page's max concurrent series count of 3).
// Color follows the entity across every chart on this page: opened/billed/NPS
// always slot 1 (blue), closed/invoiced always slot 2 (orange), received always
// slot 3 (aqua), estimated always slot 4 (yellow) — never reassigned when a chart
// shows fewer series than another.
const SERIES = { blue: "#2a78d6", orange: "#eb6834", aqua: "#1baf7a", yellow: "#eda100" };
const GRID = "#e2e8f0";
const AXIS_TEXT = "#64748b";

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}
function defaultStart() {
  const d = new Date();
  d.setMonth(d.getMonth() - 11);
  d.setDate(1);
  return isoDate(d);
}
function defaultEnd() {
  return isoDate(new Date());
}
function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
}
function currency(n) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
}
function compact(n) {
  return new Intl.NumberFormat(undefined, { notation: "compact" }).format(n);
}

export default function Reports() {
  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate] = useState(defaultEnd());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (s, e) => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports", { params: { start_date: s, end_date: e } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(startDate, endDate); }, []);

  const monthly = useMemo(
    () => (data?.monthly || []).map((m) => ({ ...m, label: monthLabel(m.month) })),
    [data]
  );
  const pipeline = useMemo(
    () => (data?.pipeline_snapshot || []).map((p) => ({ ...p, label: `Step ${p.step_number}` })),
    [data]
  );

  return (
    <div className="p-6 sm:p-10 max-w-[1400px] mx-auto page-enter">
      <header className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-gradient-to-br from-indigo-50 via-blue-50/60 to-cyan-50/50 p-6 sm:p-8">
        <div className="text-[11px] uppercase tracking-widest text-primary/70 font-semibold">Business Development</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monthly pipeline &amp; revenue overview, with a client- and faculty-wise breakdown.
        </p>

        <div className="mt-5 flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs">From</Label>
            <Input
              type="date" data-testid="reports-start-date" value={startDate} max={endDate}
              onChange={(e) => setStartDate(e.target.value)} className="mt-1 w-[160px]"
            />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input
              type="date" data-testid="reports-end-date" value={endDate} min={startDate}
              onChange={(e) => setEndDate(e.target.value)} className="mt-1 w-[160px]"
            />
          </div>
          <Button data-testid="reports-apply" onClick={() => load(startDate, endDate)} disabled={loading}>
            Apply
          </Button>
        </div>
      </header>

      {loading && <div className="text-sm text-muted-foreground mt-8">Loading report...</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-8 gap-4 mt-8" data-testid="reports-totals">
            <Stat label="Opened" value={data.totals.programs_opened} icon={<TrendingUp className="w-4 h-4" />} />
            <Stat label="Closed" value={data.totals.programs_closed} icon={<CheckCircle2 className="w-4 h-4" />} />
            <Stat label="Estimated" value={currency(data.totals.estimated_revenue)} icon={<Target className="w-4 h-4" />} />
            <Stat label="Billed" value={currency(data.totals.billing_amount)} icon={<Wallet className="w-4 h-4" />} />
            <Stat label="Invoiced" value={currency(data.totals.invoice_amount)} icon={<FileText className="w-4 h-4" />} />
            <Stat label="Received" value={currency(data.totals.payment_amount)} icon={<IndianRupee className="w-4 h-4" />} />
            <Stat label="Avg NPS" value={data.totals.avg_nps ?? "-"} icon={<Smile className="w-4 h-4" />} />
            <Stat label="SLA Breaches" value={data.totals.sla_breaches} icon={<AlertTriangle className="w-4 h-4" />} />
          </div>

          <Card className="mt-8 border-slate-200/80">
            <CardContent className="p-5">
              <h2 className="font-heading text-base font-semibold">Active Pipeline — Programs per Step</h2>
              <p className="text-xs text-muted-foreground mt-0.5 mb-4">Live snapshot, independent of the date range above.</p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={pipeline} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={{ stroke: GRID }} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={false} tickLine={false} width={28} />
                  <Tooltip formatter={(v) => [v, "Programs"]} labelFormatter={(l, p) => p?.[0]?.payload?.title || l} />
                  <Bar dataKey="count" name="Programs" fill={SERIES.blue} radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <Card className="border-slate-200/80">
              <CardContent className="p-5">
                <h2 className="font-heading text-base font-semibold">Programs Opened vs Closed</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={monthly} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={{ stroke: GRID }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="programs_opened" name="Opened" fill={SERIES.blue} radius={[4, 4, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="programs_closed" name="Closed" fill={SERIES.orange} radius={[4, 4, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-slate-200/80">
              <CardContent className="p-5">
                <h2 className="font-heading text-base font-semibold">Average NPS</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthly} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={{ stroke: GRID }} tickLine={false} />
                    <YAxis domain={[-100, 100]} tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={false} tickLine={false} width={32} />
                    <Tooltip />
                    <Line type="monotone" dataKey="avg_nps" name="Avg NPS" stroke={SERIES.blue} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6 border-slate-200/80">
            <CardContent className="p-5">
              <h2 className="font-heading text-base font-semibold">Revenue — Billed, Invoiced &amp; Received</h2>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthly} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={{ stroke: GRID }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={false} tickLine={false} width={56} tickFormatter={compact} />
                  <Tooltip formatter={(v) => currency(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="billing_amount" name="Billed" stroke={SERIES.blue} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="invoice_amount" name="Invoiced" stroke={SERIES.orange} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="payment_amount" name="Received" stroke={SERIES.aqua} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="mt-6 border-slate-200/80">
            <CardContent className="p-5">
              <h2 className="font-heading text-base font-semibold">Estimated vs Received Revenue</h2>
              <p className="text-xs text-muted-foreground mt-0.5 mb-4">
                Estimated revenue is logged at Step 1 (Sales Opportunity); received is confirmed payment at Step 9.
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthly} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={{ stroke: GRID }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: AXIS_TEXT }} axisLine={false} tickLine={false} width={56} tickFormatter={compact} />
                  <Tooltip formatter={(v) => currency(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="estimated_revenue" name="Estimated" stroke={SERIES.yellow} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="payment_amount" name="Received" stroke={SERIES.aqua} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="mt-6 border-slate-200/80">
            <CardContent className="p-0">
              <div className="p-5 pb-0">
                <h2 className="font-heading text-base font-semibold">Client-wise Breakdown</h2>
                <p className="text-xs text-muted-foreground mt-0.5 mb-2">Grouped by client name as recorded on each program.</p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Programs</TableHead>
                      <TableHead className="text-right">Active</TableHead>
                      <TableHead className="text-right">Closed</TableHead>
                      <TableHead className="text-right">Estimated</TableHead>
                      <TableHead className="text-right">Billed</TableHead>
                      <TableHead className="text-right">Invoiced</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead className="text-right">Avg NPS</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.by_client || []).map((c) => (
                      <TableRow key={c.client_name} data-testid={`report-client-${c.client_name}`}>
                        <TableCell className="font-medium">{c.client_name}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.program_count}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.active_count}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.closed_count}</TableCell>
                        <TableCell className="text-right tabular-nums">{currency(c.estimated_revenue)}</TableCell>
                        <TableCell className="text-right tabular-nums">{currency(c.billing_amount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{currency(c.invoice_amount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{currency(c.payment_amount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.avg_nps ?? "-"}</TableCell>
                      </TableRow>
                    ))}
                    {(data.by_client || []).length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-8">No programs in this range.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-6 border-slate-200/80">
            <CardContent className="p-0">
              <div className="p-5 pb-0">
                <h2 className="font-heading text-base font-semibold">Program Delivery Details</h2>
                <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                  Actual training dates (Step 6), by who delivered it — not the originally planned dates from Step 1.
                </p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Program</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Faculty</TableHead>
                      <TableHead>Start</TableHead>
                      <TableHead>End</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.program_delivery || []).map((d) => (
                      <TableRow key={d.program_id} data-testid={`report-delivery-${d.program_id}`}>
                        <TableCell className="font-medium">{d.training_title}</TableCell>
                        <TableCell>{d.client_name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{(d.faculty_names || []).join(", ") || "-"}</TableCell>
                        <TableCell className="tabular-nums">{d.training_start}</TableCell>
                        <TableCell className="tabular-nums">{d.training_end || "-"}</TableCell>
                        <TableCell>
                          <Badge className={d.status === "closed"
                            ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                            : "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-100"}>
                            {d.status.toUpperCase()}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(data.program_delivery || []).length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No training delivered in this range.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-6 border-slate-200/80">
            <CardContent className="p-0">
              <div className="p-5 pb-0">
                <h2 className="font-heading text-base font-semibold">Purchase Order Status</h2>
                <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                  Programs that have reached Step 8 (Purchase Order Follow-up) in this range — raised or still pending.
                </p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Program</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>PO Number</TableHead>
                      <TableHead className="text-right">PO Amount</TableHead>
                      <TableHead>PO Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.po_status || []).map((po) => (
                      <TableRow key={po.program_id} data-testid={`report-po-${po.program_id}`}>
                        <TableCell className="font-medium">{po.training_title}</TableCell>
                        <TableCell>{po.client_name}</TableCell>
                        <TableCell className="tabular-nums">{po.po_number || "-"}</TableCell>
                        <TableCell className="text-right tabular-nums">{po.po_amount ? currency(po.po_amount) : "-"}</TableCell>
                        <TableCell className="tabular-nums">{po.po_date || "-"}</TableCell>
                        <TableCell>
                          <Badge className={po.status === "raised"
                            ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                            : "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100"}>
                            {po.status.toUpperCase()}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(data.po_status || []).length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No programs have reached the PO step in this range.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-6 mb-4 border-slate-200/80">
            <CardContent className="p-0">
              <div className="p-5 pb-0">
                <h2 className="font-heading text-base font-semibold">Faculty Utilization</h2>
                <p className="text-xs text-muted-foreground mt-0.5 mb-2">Programs whose training dates overlap the selected range.</p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Faculty</TableHead>
                      <TableHead className="text-right">Programs</TableHead>
                      <TableHead>Clients</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.by_faculty || []).map((f) => (
                      <TableRow key={f.faculty_name} data-testid={`report-faculty-${f.faculty_name}`}>
                        <TableCell className="font-medium">{f.faculty_name}</TableCell>
                        <TableCell className="text-right tabular-nums">{f.program_count}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{f.clients.join(", ")}</TableCell>
                      </TableRow>
                    ))}
                    {(data.by_faculty || []).length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-8">No faculty assignments in this range.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <Card className="border-slate-200/80 shadow-soft">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground truncate">{label}</div>
            <div className="text-xl font-heading font-semibold tracking-tight mt-1.5 text-slate-900 tabular-nums truncate">{value}</div>
          </div>
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-md shadow-blue-500/25">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
